import {adminUrlBuilder} from "@app/api/utils";
import {
  ICheckRecurringEventConflictsResponse,
  ICreateRecurringEventDto,
  ICreateRecurringEventResponse,
  IGetRecurringAvailableSlotsResponse,
  IRecurringEvent,
  IUpdateRecurringEventDto, RecurringEventFrequency
} from '@torim-app/types';

export class RecurringEventsApi {
  static readonly path = 'recurring-events';

  /**
   * Check for conflicts before creating a recurring event
   */
  static async checkConflicts(
    body: ICreateRecurringEventDto
  ): Promise<ICheckRecurringEventConflictsResponse> {
    const url = adminUrlBuilder({}, `${RecurringEventsApi.path}/check-conflicts`);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok)
      throw "Failed to check conflicts";

    return await response.json();
  }

  /**
   * Create a new recurring manual event
   */
  static async createRecurringEvent(
    body: ICreateRecurringEventDto
  ): Promise<ICreateRecurringEventResponse> {
    const url = adminUrlBuilder({}, RecurringEventsApi.path);
    const response = await fetch(url, {
      method: 'post',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      // Same mapping as AppointmentsApi.addManualEvent, so both booking flows surface
      // the plan-limit error identically.
      if (response.status === 422)
        throw "Reached Limit";
      throw "Failed to create recurring event";
    }

    return await response.json();
  }

  /**
   * Get all recurring events for the business
   */
  static async getRecurringEvents(
    activeOnly: boolean = true,
    providingClient?: string
  ): Promise<IRecurringEvent[]> {
    const params: any = {activeOnly};
    if (providingClient) params.providingClient = providingClient;

    const url = adminUrlBuilder(params, RecurringEventsApi.path);
    const response = await fetch(url, {
      method: 'get',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!response.ok)
      throw "Failed to get recurring events";

    return await response.json();
  }

  /**
   * Update entire series (all future occurrences)
   */
  static async updateEntireSeries(
    recurringEventId: string,
    updates: IUpdateRecurringEventDto
  ): Promise<void> {
    const url = adminUrlBuilder({}, `${RecurringEventsApi.path}/series/${recurringEventId}`);
    const response = await fetch(url, {
      method: 'put',
      headers: {
        'content-type': 'application/json'
      },
      body: JSON.stringify(updates)
    });

    if (!response.ok)
      throw "Failed to update series";
  }

  /**
   * Delete entire series (all future occurrences)
   */
  static async deleteEntireSeries(recurringEventId: string): Promise<void> {
    const url = adminUrlBuilder({}, `${RecurringEventsApi.path}/series/${recurringEventId}`);
    const response = await fetch(url, {
      method: 'delete',
      headers: {
        'content-type': 'application/json'
      }
    });

    if (!response.ok)
      throw "Failed to delete series";
  }

  async getAvailableSlots(params: {
    bid: string;
    startDate: string;
    numberOfAppointments: number;
    frequency: RecurringEventFrequency;
    providingClient: string;
    serviceIds: string[];
  }): Promise<IGetRecurringAvailableSlotsResponse> {
    const url = adminUrlBuilder({
      bid: params.bid,
      startDate: params.startDate,
      numberOfAppointments: params.numberOfAppointments.toString(),
      frequency: params.frequency,
      providingClient: params.providingClient,
      serviceIds: params.serviceIds.join(',')
    }, `${RecurringEventsApi.path}/check-conflicts`);

    const response = await fetch(url, {
      method: 'GET',
      headers: {'Content-Type': 'application/json'}
    });

    if (!response.ok) {
      throw new Error('Failed to get available slots');
    }

    return response.json();
  }

}
